import React, { useState } from "react";
import { Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRADE_CATS } from "@/lib/trades";

export const tradeLabel = (u) => (u?.trade_categories?.length ? u.trade_categories : [u?.trade_category]).filter(Boolean).join(" · ");

export const TradeCategoryPicker = ({ user }) => {
  const { refresh } = useAuth();
  const current = user?.trade_categories?.length ? user.trade_categories : [user?.trade_category].filter(Boolean);
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState(current);
  const [busy, setBusy] = useState(false);

  const toggle = (c) => setSel((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const save = async () => {
    if (sel.length === 0) { toast.error("Pick at least one trade"); return; }
    setBusy(true);
    try {
      await api.patch("/me/trade-categories", { trade_categories: sel });
      await refresh();
      toast.success(`You now cover ${sel.join(", ")} — offers in ${sel.length > 1 ? "all of these trades" : "this trade"} will come to you`);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update your trades");
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <button type="button" data-testid="trade-category-edit" onClick={() => { setSel(current); setEditing(true); }}
        style={{ background: "none", border: 0, padding: 0, color: "inherit", font: "inherit", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span data-testid="trade-category-current">{current.join(" · ") || "Choose your trades"}</span>
        <Pencil size={13} style={{ color: "var(--rose)" }} />
      </button>
    );
  }

  return (
    <div className="card" data-testid="trade-category-editor" style={{ marginTop: 10, padding: 18 }}>
      <div className="od-eyebrow" style={{ marginBottom: 10 }}>Trades you cover — pick as many as apply</div>
      <div className="pillrow" style={{ marginBottom: 14 }}>
        {TRADE_CATS.map((c) => (
          <div key={c} role="checkbox" aria-checked={sel.includes(c)} data-testid={`trade-pill-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`pill ${sel.includes(c) ? "on" : ""}`} onClick={() => toggle(c)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {sel.includes(c) && <Check size={13} />}{c}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button type="button" className="od-btn-primary" data-testid="trade-category-save" onClick={save} disabled={busy} style={{ padding: "8px 18px", fontSize: ".88rem" }}>{busy ? "Saving…" : `Save ${sel.length || ""} ${sel.length === 1 ? "trade" : "trades"}`}</button>
        <button type="button" data-testid="trade-category-cancel" onClick={() => setEditing(false)} style={{ background: "none", border: 0, color: "var(--taupe)", cursor: "pointer", fontSize: ".88rem" }}>Cancel</button>
      </div>
    </div>
  );
};
